import React, { useState, useEffect, useRef } from 'react';
import ReportSoalButton from '../LanguageLearning/ReportSoalButton';
import PracticeCloseButton from '../Practice/PracticeCloseButton';
import { ShortAnswer, EssayWriting, OptionlessFillBlank, QuestionAndAnswer } from './index';
import DarkModeToggle from '../DarkModeToggle';

// changed by Cursor: detect webview for back button positioning
const isWebView = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  if (localStorage.getItem('isWebView') === 'true') {
    return true;
  }
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('webview') === 'true') {
    return true;
  }
  if (window.ReactNativeWebView) {
    return true;
  }
  if (window.__WEBVIEW__ || window.isWebView) {
    return true;
  }
  if (window.webkit && window.webkit.messageHandlers && Object.keys(window.webkit.messageHandlers).length > 0) {
    return true;
  }
  const userAgent = navigator.userAgent || '';
  const userAgentLower = userAgent.toLowerCase();
  if (userAgentLower.includes('wv') && !userAgentLower.includes('chrome')) {
    return true;
  }
  return false;
};

const WritingPractice = ({ questions = [], onComplete, onClose, hideCompletedScreen = false, sessionData = null, langCode = null, practiceTitle = 'Writing Practice', isSubmitting = false }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [isCompleted, setIsCompleted] = useState(false);
  const hasCalledOnComplete = useRef(false);
  const questionStartTimeRef = useRef(null);
  const prevIsSubmittingRef = useRef(false);

  const currentQuestion = questions[currentQuestionIndex];

  // Reset answers when questions/session change, or restore from localStorage for the same session
  useEffect(() => {
    if (questions.length === 0) return;

    const sessionId = sessionData?.['session-id'];
    const key = sessionId ? `writing-answers-${sessionId}` : null;

    if (key) {
      try {
        const saved = localStorage.getItem(key);
        if (saved) {
          const { answers: sa, currentIndex: si } = JSON.parse(saved);
          if (Array.isArray(sa) && sa.length > 0) {
            setAnswers(sa);
            setCurrentQuestionIndex(Math.min(si || 0, questions.length - 1));
            setIsCompleted(false);
            hasCalledOnComplete.current = false;
            questionStartTimeRef.current = null;
            return;
          }
        }
      } catch (_) {}
    }

    setAnswers([]);
    setCurrentQuestionIndex(0);
    setIsCompleted(false);
    hasCalledOnComplete.current = false;
    questionStartTimeRef.current = null;
  }, [questions.length, sessionData?.['session-id']]);

  // Persist answers + currentIndex whenever they change
  useEffect(() => {
    const sessionId = sessionData?.['session-id'];
    const key = sessionId ? `writing-answers-${sessionId}` : null;
    if (!key || answers.length === 0) return;
    try {
      localStorage.setItem(key, JSON.stringify({ answers, currentIndex: currentQuestionIndex }));
    } catch (_) {}
  }, [answers, currentQuestionIndex, sessionData?.['session-id']]);

  // Reset hasCalledOnComplete when submission fails (isSubmitting: true → false without navigation)
  useEffect(() => {
    if (prevIsSubmittingRef.current && !isSubmitting && hasCalledOnComplete.current) {
      hasCalledOnComplete.current = false;
      setIsCompleted(false);
    }
    prevIsSubmittingRef.current = isSubmitting;
  }, [isSubmitting]);

  // Track question start time when question changes
  useEffect(() => {
    if (currentQuestion) {
      questionStartTimeRef.current = Date.now();
      console.log(`⏱️ Writing Question ${currentQuestionIndex + 1} started at:`, questionStartTimeRef.current);
    }
  }, [currentQuestionIndex, currentQuestion]);

  // changed by Cursor: auto scroll ke atas setiap ganti soal (terutama mobile)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [currentQuestionIndex]);

  const handleAnswer = (answerData) => {
    const isLastQuestion = currentQuestionIndex === questions.length - 1;
    
    // Calculate duration for current question (in milliseconds)
    let duration = 0;
    if (questionStartTimeRef.current) {
      const endTime = Date.now();
      duration = endTime - questionStartTimeRef.current; // Keep in milliseconds
      console.log(`⏱️ Writing Question ${currentQuestionIndex + 1} duration:`, {
        startTime: questionStartTimeRef.current,
        endTime,
        durationMs: duration,
      });
    }
    
    // Add duration to answerData
    const answerWithDuration = {
      ...answerData,
      duration: duration,
    };
    
    setAnswers(prevAnswers => {
      const newAnswers = [...prevAnswers, answerWithDuration];
      
      // If this is the last question, immediately call onComplete with all answers
      if (isLastQuestion && onComplete && !hasCalledOnComplete.current) {
        hasCalledOnComplete.current = true;
        setIsCompleted(true);
        // Call onComplete immediately with the complete answers array
        onComplete(newAnswers);
      }
      
      return newAnswers;
    });
    
    // Reset start time for next question
    questionStartTimeRef.current = null;
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      // Reset start time for next question
      questionStartTimeRef.current = null;
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
    // For last question, completion is handled in handleAnswer
  };

  // Map backend question type to component type
  const mapQuestionType = (backendType) => {
    const typeMap = {
      // Short answer types
      'short-answer': 'short',
      'short-answer-writing': 'short',
      'short': 'short',
      
      // Essay types
      'essay': 'essay',
      'essay-writing': 'essay',
      
      // Fill in the blank types
      'optionless-fib': 'fillblank',
      'fib': 'fillblank',
      'fill-blank': 'fillblank',
      'fill-in-the-blank': 'fillblank',
      
      // Question and Answer
      'qna': 'qna',
    };
    return typeMap[backendType] || backendType;
  };

  const renderQuestion = () => {
    if (!currentQuestion) return null;

    const questionType = mapQuestionType(currentQuestion.type);
    const questionId = currentQuestion['soal-id'] || currentQuestion.id || currentQuestionIndex;
    
    // Use langCode from prop (must be provided, no default)
    // If langCode is null/undefined, umlaut will not show (which is correct for English)
    
    // For passage questions, add passage text from sessionData
    let questionWithPassage = { ...currentQuestion };
    if (questionType === 'qna' && sessionData && sessionData.passage) {
      questionWithPassage.passage = sessionData.passage;
    }
    
    const commonProps = {
      question: { ...questionWithPassage, number: currentQuestionIndex + 1 },
      onAnswer: handleAnswer,
      onNext: handleNext,
      sessionData: sessionData,
      langCode: langCode // Pass langCode as-is (null for English, 'de' for German)
    };

    // Use key prop to force re-mount when question changes, ensuring state is reset
    switch (questionType) {
      case 'short':
        return <ShortAnswer key={questionId} {...commonProps} />;
      case 'essay':
        return <EssayWriting key={questionId} {...commonProps} />;
      case 'fillblank':
        return <OptionlessFillBlank key={questionId} {...commonProps} />;
      case 'qna':
        return <QuestionAndAnswer key={questionId} {...commonProps} />;
      default:
        return <div>Unknown question type: {currentQuestion.type}</div>;
    }
  };

  if (isCompleted && !hideCompletedScreen) {
    const correctCount = answers.filter(a => a.isCorrect).length;
    const totalQuestions = questions.length;
    const score = Math.round((correctCount / totalQuestions) * 100);

    return (
      <div className="flex items-center justify-center min-h-[400px] p-12 max-md:py-8 max-md:px-4">
        <div className="text-center max-w-[780px]">
          <h2 className="text-3xl font-bold text-[var(--text-primary)] mb-6 m-0 max-md:text-2xl max-md:mb-5">
            Practice Completed!
          </h2>
          <div className="mb-8 max-md:mb-6">
            <div className="text-6xl font-bold bg-gradient-to-br from-[#12766d] to-[#236463] bg-clip-text text-transparent mb-2 max-md:text-5xl max-md:mb-1">
              {score}%
            </div>
            <div className="text-lg font-semibold text-[var(--text-primary)] max-md:text-base">
              {correctCount} out of {totalQuestions} correct
            </div>
          </div>
          <div className="flex gap-3 justify-center max-md:flex-col max-md:gap-2">
            <button
              type="button"
              className="py-3.5 px-10 bg-gradient-to-br from-[#fcb319] to-[#f5a500] text-[var(--text-primary)] border-none rounded-xl text-base font-semibold cursor-pointer transition-all duration-300 shadow-[0_2px_4px_rgba(252,179,25,0.2)] hover:from-[#f5a500] hover:to-[#e69500] hover:shadow-[0_4px_8px_rgba(252,179,25,0.3)] hover:-translate-y-px max-md:w-full max-md:py-3.5 max-md:px-6 max-md:text-[15px] max-md:min-h-[44px] dark:text-[#1d4746]"
              onClick={() => {
                setCurrentQuestionIndex(0);
                setAnswers([]);
                setIsCompleted(false);
              }}
            >
              Restart Practice
            </button>
            {onClose && (
              <PracticeCloseButton onClose={onClose} variant="text" confirmMessage="Anda akan kembali ke halaman sebelumnya." />
            )}
          </div>
        </div>
      </div>
    );
  }

  // changed by Cursor: detect webview for back button positioning
  const inWebView = isWebView();

  const prosetId = currentQuestion?.['proset-id'] || currentQuestion?.prosetId || currentQuestion?.proset_id || null;
  const soalId = currentQuestion?.['soal-id'] || currentQuestion?.soalId || currentQuestion?.id || null;

  return (
    <div className="w-full max-w-[1560px] md:max-w-[1061px] mx-auto p-6 md:p-4 md:px-5 bg-[var(--card-bg)] rounded-2xl shadow-[var(--shadow-sm)] flex flex-col relative max-md:max-w-none max-md:p-4 max-md:px-3 max-md:pb-[140px] max-md:rounded-none max-md:shadow-none max-md:min-h-[100dvh]">
      {/* Back left, Title center, Report right - same row */}
      <div className="relative flex items-center justify-center w-full mb-4 gap-2 min-h-[44px] [&_.practice-close-button]:mb-0">
        <div className="absolute left-0 shrink-0 z-10">
          {onClose ? (
            <PracticeCloseButton onClose={onClose} inWebView={inWebView} />
          ) : (
            <span />
          )}
        </div>
        <h2 className="m-0 text-lg font-semibold text-[var(--text-primary)] text-center leading-tight max-md:text-xs max-md:max-w-[calc(100%-120px)] max-md:truncate">
          {practiceTitle}
        </h2>
        <div className="absolute right-0 flex items-center gap-2 shrink-0 z-10">
          <DarkModeToggle variant="practice" />
          <ReportSoalButton type="writing" prosetId={prosetId} soalId={soalId} allowReReport={false} />
        </div>
      </div>
      <div className="mb-6 max-md:mb-5 md:mb-4">
        <div className="flex justify-between mb-2 text-sm font-semibold text-[var(--text-primary)] max-md:text-[13px]">
          <span>Question {currentQuestionIndex + 1} of {questions.length}</span>
        </div>
        <div className="w-full h-2 bg-[rgba(18,118,109,0.1)] dark:bg-[rgba(148,163,184,0.15)] rounded-full overflow-hidden max-md:h-1.5">
          <div
            className="h-full bg-gradient-to-r from-[#12766d] via-[#236463] to-[#1d4746] dark:from-[#14b8a6] dark:via-[#0d9488] dark:to-[#0f766e] rounded-full transition-[width] duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
            style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>

      {renderQuestion()}
    </div>
  );
};

export default WritingPractice;

